import React, { useState } from 'react'
import { Palette, Shuffle } from 'lucide-react'

interface ColorSelectorProps {
  primaryColor: string
  secondaryColor: string
  textColor: string
  wrapperColor?: string
  wrapperOpacity?: number
  onPrimaryColorChange: (color: string) => void
  onSecondaryColorChange: (color: string) => void
  onTextColorChange: (color: string) => void
  onWrapperColorChange?: (color: string) => void
  onWrapperOpacityChange?: (opacity: number) => void
  onAllColorsChange?: (colors: { primaryColor: string; secondaryColor: string; textColor: string; wrapperColor?: string; wrapperOpacity?: number }) => void
  colorMode?: 'gradient' | 'structured'
  onColorModeChange?: (mode: 'gradient' | 'structured') => void
  disabled?: boolean
}

// Predefined color combinations - with specific requested combos
const colorCombinations = [
  // Specific Requested Combinations
  { primary: '#ffffff', secondary: '#d0e6fb', wrapper: '#ffffff', name: 'Cloud', textColor: '#1d1616' },
  { primary: '#7ec09f', secondary: '#ffffff', wrapper: '#ffffff', name: 'Mint', textColor: '#1d1616' },
  { primary: '#b69edf', secondary: '#ffffff', wrapper: '#ffffff', name: 'Lavender', textColor: '#1d1616' },
  { primary: '#edc472', secondary: '#ffffff', wrapper: '#ffffff', name: 'Honey', textColor: '#1d1616' },
  { primary: '#6f8fae', secondary: '#ffffff', wrapper: '#d7dde4', name: 'Sky', textColor: '#000000' },
  { primary: '#ecedf1', secondary: '#ffffff', wrapper: '#ffffff', name: 'Pearl', textColor: '#1d1616' },
  { primary: '#daebf6', secondary: '#537ac9', wrapper: '#ffffff', name: 'Ocean', textColor: '#1d1616' },
  { primary: '#527ac9', secondary: '#ffffff', wrapper: '#7ec09f', name: 'Royal', textColor: '#ffffff' },
  { primary: '#ffc680', secondary: '#ffffff', wrapper: '#83c3ab', name: 'Peach', textColor: '#1F2937' },
  { primary: '#b08699', secondary: '#ffffff', wrapper: '#393e38', name: 'Mauve', textColor: '#FFFFFF' },
  { primary: '#9cb276', secondary: '#F0F4EC', wrapper: '#ffffff', name: 'Sage Green', textColor: '#000000' },
  { primary: '#7ec09f', secondary: '#000000', wrapper: '#7e8ba0', name: 'Mint Dark', textColor: '#FFFFFF' },
  { primary: '#edc472', secondary: '#232421', wrapper: '#7e8ba0', name: 'Honey Dark', textColor: '#FFFFFF' },
  { primary: '#ffffff', secondary: '#526075', wrapper: '#7e8ba0', name: 'Arctic', textColor: '#000000' },
  { primary: '#F5F5DC', secondary: '#d7cdc1', wrapper: '#9e7f76', name: 'Beige', textColor: '#0e131a' },
  
  // Classic Masculines
  
  // Earth Tones
  
  // Soft Combinations
  { primary: '#B5C9C3', secondary: '#FDF0E7', wrapper: '#8B7D6B', name: 'Mint & Peach', textColor: '#1F2937' },
  
  // Sophisticated Neutrals
  { primary: '#8E9AAF', secondary: '#FAEEE7', wrapper: '#AA8F7E', name: 'Slate Blue & Apricot', textColor: '#1F2937' },
  { primary: '#A8A5B5', secondary: '#F5F3E9', wrapper: '#8B7E6A', name: 'Cool Gray & Warm White', textColor: '#1F2937' }
]

export const ColorSelector: React.FC<ColorSelectorProps> = ({
  primaryColor,
  secondaryColor,
  textColor,
  wrapperColor = '#4B4B4B',
  wrapperOpacity = 1,
  onPrimaryColorChange,
  onSecondaryColorChange,
  onTextColorChange,
  onWrapperColorChange,
  onWrapperOpacityChange,
  onAllColorsChange,
  colorMode = 'gradient',
  onColorModeChange,
  disabled = false
}) => {
  const [isSwapped, setIsSwapped] = useState(false)
  
  // Debug on mount
  React.useEffect(() => {
    console.log('🎨 ColorSelector mounted with props:', {
      primaryColor,
      secondaryColor,
      textColor,
      colorMode,
      disabled,
      hasCallbacks: {
        onPrimaryColorChange: !!onPrimaryColorChange,
        onSecondaryColorChange: !!onSecondaryColorChange,
        onTextColorChange: !!onTextColorChange,
        onAllColorsChange: !!onAllColorsChange,
        onColorModeChange: !!onColorModeChange
      }
    })
  }, [])

  const handleSwapColors = () => {
    const temp = primaryColor
    if (onAllColorsChange) {
      onAllColorsChange({
        primaryColor: secondaryColor,
        secondaryColor: temp,
        textColor: textColor,
        wrapperColor: wrapperColor,
        wrapperOpacity: wrapperOpacity
      })
    } else {
      onPrimaryColorChange(secondaryColor)
      onSecondaryColorChange(temp)
    }
    setIsSwapped(!isSwapped)
  }

  const handleColorCombinationSelect = (combo: typeof colorCombinations[0]) => {
    console.log('🎨 Color combination selected:', combo)
    console.log('🎨 isSwapped:', isSwapped)
    
    const newPrimary = isSwapped ? combo.secondary : combo.primary
    const newSecondary = isSwapped ? combo.primary : combo.secondary
    
    // Use predefined text color if available, otherwise calculate based on brightness
    const newTextColor = combo.textColor || (() => {
      const r = parseInt(newSecondary.substring(1, 3), 16)
      const g = parseInt(newSecondary.substring(3, 5), 16) 
      const b = parseInt(newSecondary.substring(5, 7), 16)
      const brightness = (r * 299 + g * 587 + b * 114) / 1000
      return brightness > 128 ? '#1F2937' : '#FFFFFF'
    })()
    
    console.log('🎨 New colors to set:', { newPrimary, newSecondary, newTextColor })
    
    // If we have the all-colors callback, use it for atomic updates
    if (onAllColorsChange) {
      console.log('🎨 Using onAllColorsChange callback')
      onAllColorsChange({
        primaryColor: newPrimary,
        secondaryColor: newSecondary,
        textColor: newTextColor,
        wrapperColor: combo.wrapper,
        wrapperOpacity: 1
      })
    } else {
      console.log('🎨 Using individual color callbacks')
      onPrimaryColorChange(newPrimary)
      onSecondaryColorChange(newSecondary)
      onTextColorChange(newTextColor)
      if (onWrapperColorChange) onWrapperColorChange(combo.wrapper)
      if (onWrapperOpacityChange) onWrapperOpacityChange(1)
    }
  }

  return (
    <div className="space-y-3">
      {/* Color Mode Toggle */}
      {onColorModeChange && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="color-mode"
                value="structured"
                checked={colorMode === 'structured'}
                onChange={() => onColorModeChange('structured')}
                disabled={disabled}
                className="mr-2 mt-0.5 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Strukturiert</span>
                <div className="text-xs text-gray-500">Klare Trennung</div>
              </div>
            </label>
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="color-mode"
                value="gradient"
                checked={colorMode === 'gradient'}
                onChange={() => onColorModeChange('gradient')}
                disabled={disabled}
                className="mr-2 mt-0.5 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Farbverlauf</span>
                <div className="text-xs text-gray-500">Weicher Übergang</div>
              </div>
            </label>
          </div>
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg">

      {/* Grid Layout: Palette and Color Inputs Side by Side */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left Column: Color Palette */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Farbpalette
          </label>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              {colorCombinations.map((combo, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleColorCombinationSelect(combo)}
                  disabled={disabled}
                  className="relative h-8 rounded-md overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all hover:shadow-md shadow-sm disabled:opacity-50"
                  title={combo.name}
                >
                  {colorMode === 'gradient' ? (
                    <>
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(to bottom, ${isSwapped ? combo.secondary : combo.primary}, ${isSwapped ? combo.primary : combo.secondary})`
                        }}
                      />
                      <div
                        className="absolute left-1/4 right-1/4 top-1/4 h-1/2 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: combo.wrapper }}
                      >
                        <span 
                          className="text-xs font-bold"
                          style={{ 
                            color: combo.textColor || (() => {
                              const color = combo.wrapper.replace('#', '')
                              const r = parseInt(color.substr(0, 2), 16)
                              const g = parseInt(color.substr(2, 2), 16)
                              const b = parseInt(color.substr(4, 2), 16)
                              const brightness = (r * 299 + g * 587 + b * 114) / 1000
                              return brightness > 128 ? '#1F2937' : '#FFFFFF'
                            })()
                          }}
                        >
                          A
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="absolute inset-0 top-0 h-1/2"
                        style={{ backgroundColor: isSwapped ? combo.secondary : combo.primary }}
                      />
                      <div
                        className="absolute inset-0 top-1/2"
                        style={{ backgroundColor: isSwapped ? combo.primary : combo.secondary }}
                      />
                      <div
                        className="absolute left-1/4 right-1/4 top-1/4 h-1/2 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: combo.wrapper }}
                      >
                        <span 
                          className="text-xs font-bold"
                          style={{ 
                            color: combo.textColor || (() => {
                              const color = combo.wrapper.replace('#', '')
                              const r = parseInt(color.substr(0, 2), 16)
                              const g = parseInt(color.substr(2, 2), 16)
                              const b = parseInt(color.substr(4, 2), 16)
                              const brightness = (r * 299 + g * 587 + b * 114) / 1000
                              return brightness > 128 ? '#1F2937' : '#FFFFFF'
                            })()
                          }}
                        >
                          A
                        </span>
                      </div>
                    </>
                  )}
                </button>
              ))}
            </div>
            {/* Swap Colors Button */}
            <button
              type="button"
              onClick={handleSwapColors}
              disabled={disabled}
              className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              <Shuffle className="w-3 h-3" />
              Farben tauschen
            </button>
          </div>
        </div>

        {/* Right Column: Color Inputs */}
        <div className="space-y-3">
          {/* Primary Color */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Primärfarbe
              <span className="text-gray-500 font-normal ml-1">
                {colorMode === 'gradient' ? '(Start)' : '(Oben)'}
              </span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => {
                  console.log('🎨 Primary color picker changed:', e.target.value)
                  onPrimaryColorChange(e.target.value)
                }}
                disabled={disabled}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer disabled:opacity-50"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => {
                  console.log('🎨 Primary color text input changed:', e.target.value)
                  onPrimaryColorChange(e.target.value)
                }}
                disabled={disabled}
                placeholder="#3B82F6"
                className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Secondary Color */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Sekundärfarbe
              <span className="text-gray-500 font-normal ml-1">
                {colorMode === 'gradient' ? '(Ende)' : '(Unten)'}
              </span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={secondaryColor}
                onChange={(e) => onSecondaryColorChange(e.target.value)}
                disabled={disabled}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer disabled:opacity-50"
              />
              <input
                type="text"
                value={secondaryColor}
                onChange={(e) => onSecondaryColorChange(e.target.value)}
                disabled={disabled}
                placeholder="#1F2937"
                className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Text Color */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Textfarbe
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={textColor}
                onChange={(e) => onTextColorChange(e.target.value)}
                disabled={disabled}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer disabled:opacity-50"
              />
              <input
                type="text"
                value={textColor}
                onChange={(e) => onTextColorChange(e.target.value)}
                disabled={disabled}
                placeholder="#FFFFFF"
                className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Wrapper Color */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Wrapper-Farbe
              <span className="text-gray-500 font-normal ml-1">(Profilbox)</span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={wrapperColor}
                onChange={(e) => onWrapperColorChange && onWrapperColorChange(e.target.value)}
                disabled={disabled}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer disabled:opacity-50"
              />
              <input
                type="text"
                value={wrapperColor}
                onChange={(e) => onWrapperColorChange && onWrapperColorChange(e.target.value)}
                disabled={disabled}
                placeholder="#4B4B4B"
                className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}